import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { Segment, SegmentCategory } from '@interfaces/segments/domain';
import { segmentsAdapter, segmentCategoriesAdapter } from './segmentsSlice';
import { selectSelectedMarketIds } from '@/store/slices/userSelection/selectors';
import { isAvailableInSelectedMarkets } from '@utils/markets/availability';
import { selectFiltersWithMarketLabels } from '@/store/slices/filters/selectors';
import { withCatalogLabels } from '@utils/filters/marketLabels';
import type { SegmentsAdapter } from './segmentsSlice';

const segsSelectors = segmentsAdapter.getSelectors(
  (s: RootState) => s.activeGroup.segments.segments,
);
const catsSelectors = segmentCategoriesAdapter.getSelectors(
  (s: RootState) => s.activeGroup.segments.categories,
);

export const selectAllSegments = segsSelectors.selectAll;
export const selectAllSegmentCategories = catsSelectors.selectAll;
export const selectSegmentByCode = segsSelectors.selectById;
export const selectSegmentCategoryByCode = catsSelectors.selectById;

/**
 * The segments entity state with each segment's filter and option names
 * replaced by catalog labels for the selected markets (see
 * `withCatalogLabels`). For display only; requests go by code.
 */
export const selectSegmentsWithMarketLabels = createSelector(
  [(s: RootState) => s.activeGroup.segments.segments, selectFiltersWithMarketLabels],
  (segments, filters): SegmentsAdapter => {
    const filtersByCode = new Map(filters.map((f) => [f.code, f]));
    const entities: SegmentsAdapter['entities'] = {};
    for (const code of segments.ids) {
      const seg = segments.entities[code];
      if (seg) entities[code] = { ...seg, filters: withCatalogLabels(seg.filters, filtersByCode) };
    }
    return { ids: segments.ids, entities };
  },
);

export const selectSegmentsForMarkets = createSelector(
  [selectAllSegments, selectSelectedMarketIds],
  (segments, markets) =>
    segments.filter(
      // SYSTEM segments arrive with `markets: []` meaning "all markets" —
      // the deny-on-empty rule must never reach them. Only USER segments
      // carry real applicability. Do not "fix" this into the predicate.
      (s) =>
        s.type === 'SYSTEM' ||
        isAvailableInSelectedMarkets(s.markets, markets),
    ),
);

export const selectAppliedSegmentCodes = createSelector(
  [selectAllSegments],
  (segments) => segments.filter((s) => s.isApplied).map((s) => s.code),
);

export interface SegmentPickerSection {
  category: SegmentCategory;
  segments: Segment[];
}

export const selectSegmentPickerData = createSelector(
  [selectAllSegmentCategories, selectSegmentsForMarkets],
  (categories, segments): SegmentPickerSection[] => {
    const byCategory = new Map<string, Segment[]>();
    for (const s of segments) {
      const arr = byCategory.get(s.categoryCode) ?? [];
      arr.push(s);
      byCategory.set(s.categoryCode, arr);
    }
    return categories
      .map((c) => ({ category: c, segments: byCategory.get(c.code) ?? [] }))
      .filter((sec) => sec.segments.length > 0);
  },
);
